import {sortBy} from 'lodash';
import React, {useCallback, useMemo} from 'react';
import Masonry, {ResponsiveMasonry} from 'react-responsive-masonry';
import {useSearchParams} from 'react-router-dom';
import {Body, Headline, Icon, Label} from '@lightricks/react-design-system';
import translate from '@/utils/translate';
import {
  CreatorProfileApplicationAnswer,
  ProfileProps,
} from '@/types/creatorProfile';
import {MediaFile} from '@/types/mediaFile';
import Conditional from '@/components/conditional';
import ApplicationAnswers from '@/components/creator-profile/components/application-questions';
import MetricsSummaryCard from '@/components/creator-profile/components/metrics-summary-card';
import {TABS} from '@/components/creator-profile/constants';
import CREATOR_PROFILE_TEST_IDS from '@/components/creator-profile/testIds';
import MediaFileViewer from '@/components/media-file-viewer';
import ToggleableTags from '@/components/toggleable-tags';
import {CreatorProfileTabContentProps} from '../../CreatorProfileTabContentProps';
import TabWrapper from '../tab-wrapper';
import styles from './OverviewTab.module.scss';
import CampaignHistorySummary from './components/campaign-history-summary';

const TRANSLATION_PREFIX = 'components.creator-profile.tabs.overview';
const TAGS_LIMIT = 20;

export function AboutMe({profile}: {profile: ProfileProps}) {
  const {bio} = profile;

  const tagsAndContentPreferences = useMemo(() => {
    return [
      ...profile.tags.map((tag) => (typeof tag === 'string' ? tag : tag.name)),
      ...profile.contentPreferences.map(
        (contentPreference) => contentPreference.id
      ),
    ].map((label) => translate(label));
  }, [profile.tags, profile.contentPreferences]);

  return (
    <div className={styles.aboutMeSection}>
      <Headline size="sm">{translate('About')}</Headline>
      <Conditional condition={!!bio}>
        <Body
          size="md"
          data-testid={CREATOR_PROFILE_TEST_IDS.TAB__OVERVIEW__BIO}
        >
          {bio}
        </Body>
      </Conditional>
      <ToggleableTags
        testID={
          CREATOR_PROFILE_TEST_IDS.TAB__OVERVIEW__TAGS_AND_CONTENT_PREFERENCES
        }
        tags={tagsAndContentPreferences}
        size="md"
        tagsLimit={TAGS_LIMIT}
      />
    </div>
  );
}

function Portfolio({
  profile,
  onPortfolioItemClick,
  onSeePostsClick,
}: {
  profile: ProfileProps;
  onPortfolioItemClick: (mediaItem: MediaFile) => void;
  onSeePostsClick?: () => void;
}) {
  const {portfolioMediaItems} = profile;
  const sortedPortfolioMediaItems = useMemo(
    () => sortBy(portfolioMediaItems, 'weight'),
    [portfolioMediaItems]
  );

  return (
    <div
      className={styles.portfolioSection}
      data-testid={CREATOR_PROFILE_TEST_IDS.TAB__OVERVIEW__PORTFOLIO}
    >
      <Headline size="sm">
        {translate(`${TRANSLATION_PREFIX}.portfolio`)}
      </Headline>
      <ResponsiveMasonry columnsCountBreakPoints={{350: 1, 750: 2, 900: 3}}>
        <Masonry columnsCount={3} gutter="10px">
          {sortedPortfolioMediaItems.map((mediaFile) => (
            <MediaFileViewer
              key={mediaFile.id}
              id={mediaFile.id}
              url={mediaFile.url}
              kind={mediaFile.kind}
              thumbnailUrl={mediaFile.thumbnailUrl}
              onClick={onPortfolioItemClick}
            />
          ))}
        </Masonry>
      </ResponsiveMasonry>
      <Conditional condition={!!onSeePostsClick}>
        <button
          type="button"
          className={styles.portfolioFooter}
          data-testid={
            CREATOR_PROFILE_TEST_IDS.TAB__OVERVIEW__PORTFOLIO_SEE_POSTS
          }
          onClick={onSeePostsClick}
        >
          <Label size="md">{translate('See posts')}</Label>
          <Icon
            size="small"
            appearance="neutral"
            name="Actions-Arrow-Forward-Small"
          />
        </button>
      </Conditional>
    </div>
  );
}

function OverviewTab(props: CreatorProfileTabContentProps) {
  const {profile, creatorProfileAbilities, trackButtonPressed} = props;
  const [searchParams, setSearchParams] = useSearchParams();

  const handleSeePostsClick = useCallback(() => {
    trackButtonPressed('See posts');
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('activeTabId', TABS.POSTS.id);
    setSearchParams(nextParams);
  }, [searchParams, setSearchParams, trackButtonPressed]);

  return (
    <TabWrapper
      testID={CREATOR_PROFILE_TEST_IDS.TAB__OVERVIEW}
      className={styles.overviewTabContainer}
    >
      <Conditional condition={creatorProfileAbilities.canViewPortfolio}>
        <AboutMe profile={profile} />
        <Conditional
          condition={
            creatorProfileAbilities.showApplicationAnswers &&
            !!profile.applicationAnswers
          }
        >
          <ApplicationAnswers
            title={translate(`${TRANSLATION_PREFIX}.application-questions`)}
            items={
              profile.applicationAnswers as CreatorProfileApplicationAnswer[]
            }
          />
        </Conditional>
      </Conditional>
      <Conditional condition={creatorProfileAbilities.showCampaignHistory}>
        <CampaignHistorySummary
          profile={profile}
          trackButtonPressed={trackButtonPressed}
        />
      </Conditional>
      <Conditional
        condition={
          creatorProfileAbilities.showAnalyticsAndAudienceTab &&
          profile.identities?.length > 0
        }
      >
        <MetricsSummaryCard
          identities={profile.identities}
          trackButtonPressed={trackButtonPressed}
          fallbackAvatarUrl={profile.imageUrl}
        />
      </Conditional>
      <Conditional condition={creatorProfileAbilities.canViewPortfolio}>
        <Portfolio
          profile={profile}
          onPortfolioItemClick={(mediaFile) =>
            trackButtonPressed(`Open portfolio media`, {
              flow_id: mediaFile.kind,
              flow_name: mediaFile.url,
            })
          }
          onSeePostsClick={
            creatorProfileAbilities.canViewPosts
              ? handleSeePostsClick
              : undefined
          }
        />
      </Conditional>
    </TabWrapper>
  );
}

export default OverviewTab;
